/**
 * Share-link visit id — Part A of the Baseline Metrics spec.
 *
 * The backend's /s/{campCode} resolver logs the link open and appends ?vid= to
 * the 302 it issues. This module ferries that id from the landing URL to the
 * eventual POST /auth/login, so a login can be attributed back to the link tap
 * that started it.
 *
 * Why not a cookie (which the spec originally called for): the share link is
 * served on the brand-app origin while /auth/login goes to VITE_API_URL — a
 * different host — and `withCredentials` is set nowhere in this monorepo, so a
 * cookie would never be sent. sessionStorage also survives WhatsApp's in-app
 * WebView, which was the whole reason the spec avoided client-side tracking.
 *
 * Why sessionStorage and not the `storage` helper next door: that one wraps
 * localStorage, which is shared across tabs and persists indefinitely. A visit
 * is one tab, one sitting — sessionStorage matches that lifetime exactly.
 */
const KEY = 'discovr-brand-visit-id';

const isBrowser = (): boolean => typeof window !== 'undefined';

/**
 * Copy ?vid= out of the current URL into sessionStorage. Call once at app boot,
 * BEFORE the router runs — the protected-route guard rewrites the location to
 * /login?redirect=..., which buries the vid inside an encoded param.
 *
 * After capture the param is stripped from the address bar via replaceState:
 * the id is opaque and short-lived server-side, but leaving it in the URL let
 * it flow into anything that records URLs (Sentry, Clarity, copy-pasted
 * re-shares). replaceState never navigates, so the router boots on the clean
 * URL either way.
 */
export function captureVisitId(): void {
  if (!isBrowser()) return;
  try {
    const params = new URLSearchParams(window.location.search);
    const vid = params.get('vid');
    if (!vid) return;
    window.sessionStorage.setItem(KEY, vid);
    params.delete('vid');
    const query = params.toString();
    window.history.replaceState(
      window.history.state,
      '',
      window.location.pathname + (query ? `?${query}` : '') + window.location.hash,
    );
  } catch {
    // Storage disabled (Safari private mode, embedded webview). The visit just
    // goes unattributed — never break boot over a metric.
  }
}

export function readVisitId(): string | undefined {
  if (!isBrowser()) return undefined;
  try {
    return window.sessionStorage.getItem(KEY) ?? undefined;
  } catch {
    return undefined;
  }
}

/**
 * Drop the id once it has been reported. Without this, every restored session
 * in this tab would keep re-sending the same vid for as long as the tab lives.
 */
export function clearVisitId(): void {
  if (!isBrowser()) return;
  try {
    window.sessionStorage.removeItem(KEY);
  } catch {
    // ignore
  }
}
